import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { EdgePosition, Size } from '@ts/css'
import { times } from 'lodash'
import React, { ReactNode } from 'react'

import { Theme } from '../../lib/theming/Theme'
import { ensureSizeUnit } from '../../lib/theming/units'

const { PI, cos, sin, ceil } = Math
const POLYGONAL_SECTORS = 32
const POLYGONAL_SECTOR_ANGLE = (2 * PI) / POLYGONAL_SECTORS

type Point = {
  x: number
  y: number
}

const semiCirclularPolygon = (position: EdgePosition) => {
  const axisOrientation = {
    bottom: 0,
    left: PI / -2,
    right: PI / 2,
    top: PI,
  }[position]

  const rotateToAxis = ({ x, y }: Point): Point => ({
    x: cos(axisOrientation) * x - sin(axisOrientation) * y,
    y: sin(axisOrientation) * x + cos(axisOrientation) * y,
  })

  const semiCirclePolygonalPoints = ceil(POLYGONAL_SECTORS / 2) + 1
  return times(semiCirclePolygonalPoints, index => {
    const angularDisplacement = POLYGONAL_SECTOR_ANGLE * index
    return rotateToAxis({
      x: cos(angularDisplacement),
      y: sin(angularDisplacement),
    })
  })
}

const semiCircularPath = (radius: Size, position: EdgePosition, offset: Size): string[] => {
  const points = semiCirclularPolygon(position)

  const offsetSize = ensureSizeUnit(offset)
  const { x: offsetX, y: offsetY } = {
    bottom: {
      x: offsetSize,
      y: '100%',
    },
    left: {
      x: '0%',
      y: offsetSize,
    },
    right: {
      x: '100%',
      y: offsetSize,
    },
    top: {
      x: offsetSize,
      y: '0%',
    },
  }[position]

  const calcSize = (unitLength: number) => `(${ensureSizeUnit(radius)} * ${unitLength.toFixed(3)})`
  return points.map(
    ({ x, y }) => `calc(${offsetX} + ${calcSize(x)})
                   calc(${offsetY} - ${calcSize(y)})` // X→ Y↓
  )
}

const holePunchPolygon = (radius: Size, position: EdgePosition, offset: Size): string[] => {
  const points = []

  const path = semiCircularPath(radius, position, offset)
  const injectPath = () => points.push(...path)

  points.push('0% 0%')
  if (position === 'top') injectPath()

  points.push('100% 0%')
  if (position === 'right') injectPath()

  points.push('100% 100%')
  if (position === 'bottom') injectPath()

  points.push('0% 100%')
  if (position === 'left') injectPath()

  return points
}

const useStyles = makeStyles<Theme, HolePunchStyleProps>({
  holePunch: ({ position, offset, radius }) => ({
    clipPath: `polygon(${holePunchPolygon(radius, position, offset).join(',')})`,
  }),
})

type HolePunchStyleProps = {
  height?: string
  offset: Size
  position: EdgePosition
  radius: Size
}

export type HolePunchProps = HolePunchStyleProps & { children: ReactNode }

const HolePunch = ({ children, offset, position, radius, height }: HolePunchProps) => {
  const { holePunch } = useStyles({ offset, position, radius })
  return (
    <Box className={holePunch} height={height}>
      {children}
    </Box>
  )
}

export default HolePunch
